/**
 * Client API : enveloppe {ok, code, message} (§2.2), Bearer + refresh rotatif
 * automatique, détection hors-ligne (les erreurs réseau remontent typées pour
 * que la couche offline prenne le relais).
 */
import type { ApiResponse, ErrorCode } from '@myequip/shared';
import { shrinkForUpload, uploadFilename } from './image-upload.js';
import { siteHintCookie } from './site-hint.js';
import { t } from './i18n.js';
import { fallbackMembers, storedMember, writeStoredMember } from './memberships.js';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/** Construit une URL vers une ressource publique servie par l'API. */
export function apiAssetUrl(path: string): string {
  return `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Code d'une requête refusée par le proxy pour sa taille (413 sans enveloppe JSON). */
export const PAYLOAD_TOO_LARGE = 'payload_too_large';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'network_error' | typeof PAYLOAD_TOO_LARGE,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Bus d'erreurs API : permet aux bannières globales (lecture seule Q199…)
 * de réagir sans couplage aux écrans. */
type ApiErrorListener = (code: ErrorCode | 'network_error') => void;
const errorListeners = new Set<ApiErrorListener>();

export function onApiError(listener: ApiErrorListener): () => void {
  errorListeners.add(listener);
  return () => errorListeners.delete(listener);
}

function emitApiError(code: ErrorCode | 'network_error'): void {
  for (const listener of errorListeners) listener(code);
}

/** Ce que le serveur attend pour revérifier l'identité avant une action sensible. */
export interface StepUpDetails {
  methods?: ('totp' | 'recovery_code' | 'password' | 'webauthn' | 'email_code')[];
  blocked?: 'mfa_enrollment_required' | 'password_missing';
  validitySeconds?: number;
  /** Action qui exige une vérification plus récente (double authentification) : en secondes. */
  recentSeconds?: number;
}

/**
 * Vérification d'identité (step-up) : quand l'API répond `step_up_required`,
 * le dialogue commun demande le code ou le mot de passe, puis la requête
 * d'origine est rejouée UNE fois, à l'identique. L'écran appelant ne voit rien :
 * son formulaire reste tel quel. Un seul dialogue pour des requêtes simultanées.
 */
type StepUpHandler = (details: StepUpDetails | undefined) => Promise<boolean>;
let stepUpHandler: StepUpHandler | undefined;

export function setStepUpHandler(handler: StepUpHandler): () => void {
  stepUpHandler = handler;
  return () => {
    if (stepUpHandler === handler) stepUpHandler = undefined;
  };
}

/** Message d'une vérification abandonnée : rien n'a été envoyé une seconde fois. */
export const STEP_UP_CANCELLED = 'Vérification annulée : rien n’a été modifié, votre saisie est conservée.';

/**
 * Type de session : chaque espace a SON point de refresh. Un jeton portail
 * rafraîchi contre `/auth/refresh` (interne) échouait et effaçait la session —
 * le portail « perdait » sa connexion au moindre rechargement (défaut relevé).
 */
export type SessionKind = 'internal' | 'portal' | 'platform' | 'support' | 'subcontractor';

/**
 * Espaces de stockage SÉPARÉS (décision du 18/10/2026) : ouvrir le portail
 * client ou l'espace sous-traitant dans un navigateur qui détient une session
 * interne ne la remplace plus, et inversement. L'espace interne garde ses clés
 * historiques (`fa.access`, `fa.refresh`, `fa.kind` : application, console,
 * assistance) ; le portail et le sous-traitant ont chacun les leurs.
 */
export type SessionSpace = 'internal' | 'portal' | 'subcontractor';

const SPACE_KEYS: Record<SessionSpace, { access: string; refresh: string }> = {
  internal: { access: 'fa.access', refresh: 'fa.refresh' },
  portal: { access: 'fa.portal.access', refresh: 'fa.portal.refresh' },
  subcontractor: { access: 'fa.sub.access', refresh: 'fa.sub.refresh' },
};

const REFRESH_PATH: Partial<Record<SessionKind, string>> = {
  internal: '/auth/refresh',
  portal: '/portal/auth/refresh',
  subcontractor: '/subcontractor/auth/refresh',
};

/** L'espace d'une adresse : celui dont la page lit et écrit la session. */
export function spaceForPath(pathname: string): SessionSpace {
  if (pathname === '/portail' || pathname.startsWith('/portail/')) return 'portal';
  if (pathname === '/sous-traitant' || pathname.startsWith('/sous-traitant/')) return 'subcontractor';
  return 'internal';
}

function spaceOfKind(kind: SessionKind): SessionSpace {
  return kind === 'portal' || kind === 'subcontractor' ? kind : 'internal';
}

interface TokenStore {
  accessToken?: string | undefined;
  refreshToken?: string | undefined;
  kind: SessionKind;
}

function readStore(space: SessionSpace): TokenStore {
  const keys = SPACE_KEYS[space];
  return {
    accessToken: localStorage.getItem(keys.access) ?? undefined,
    refreshToken: localStorage.getItem(keys.refresh) ?? undefined,
    kind: space === 'internal'
      ? (localStorage.getItem('fa.kind') as SessionKind | null) ?? 'internal'
      : space,
  };
}

/**
 * Une session portail ou sous-traitant posée par une version précédente dans
 * l'emplacement unique d'alors : déplacée dans son propre espace, l'emplacement
 * interne redevient libre (aucune reconnexion imposée).
 */
function migrateLegacySlot(): void {
  const legacy = localStorage.getItem('fa.kind');
  if (legacy !== 'portal' && legacy !== 'subcontractor') return;
  const target = SPACE_KEYS[legacy];
  for (const [from, to] of [['fa.access', target.access], ['fa.refresh', target.refresh]] as const) {
    const value = localStorage.getItem(from);
    if (value && !localStorage.getItem(to)) localStorage.setItem(to, value);
    localStorage.removeItem(from);
  }
  localStorage.setItem('fa.kind', 'internal');
}
migrateLegacySlot();

const stores: Record<SessionSpace, TokenStore> = {
  internal: readStore('internal'),
  portal: readStore('portal'),
  subcontractor: readStore('subcontractor'),
};

function currentSpace(): SessionSpace {
  const pathname = typeof window === 'undefined' ? undefined : window.location?.pathname;
  return pathname ? spaceForPath(pathname) : 'internal';
}

/** La session de l'espace où se trouve la page. */
function current(): TokenStore {
  return stores[currentSpace()];
}

/**
 * Pose ou efface les jetons. `kind` désigne l'espace écrit ; sans `kind`, c'est
 * celui de la page (la déconnexion du portail n'efface que le portail).
 */
export function setTokens(access?: string, refresh?: string, kind?: SessionKind): void {
  const space = kind ? spaceOfKind(kind) : currentSpace();
  const store = stores[space];
  const keys = SPACE_KEYS[space];
  store.accessToken = access;
  store.kind = kind ?? (space === 'internal' ? 'internal' : space);
  if (space === 'internal') localStorage.setItem('fa.kind', store.kind);
  if (access) localStorage.setItem(keys.access, access);
  else localStorage.removeItem(keys.access);
  if (refresh !== undefined) {
    store.refreshToken = refresh;
    if (refresh) localStorage.setItem(keys.refresh, refresh);
    else localStorage.removeItem(keys.refresh);
  }
  // Session fermée : le dernier profil connu (rechargement hors ligne) part avec.
  if (space === 'internal' && !access && refresh === '') localStorage.removeItem('fa.me');
  syncSiteHint();
}

export function hasSession(): boolean {
  const store = current();
  return Boolean(store.accessToken || store.refreshToken);
}

/** Jeton de renouvellement de l'espace courant (déconnexion de cet appareil). */
export function currentRefreshToken(): string | undefined {
  return current().refreshToken;
}

/** Aligne le témoin de la vitrine sur la session interne réellement détenue. */
function syncSiteHint(): void {
  if (typeof document === 'undefined') return;
  const internal = stores.internal;
  const cookie = siteHintCookie(window.location, internal.kind === 'internal' && Boolean(internal.refreshToken));
  if (cookie) document.cookie = cookie;
}
syncSiteHint();

/**
 * Les onglets partagent la session : chacun relit les jetons renouvelés par un
 * autre. Sans cela, un second onglet présentait un jeton de renouvellement déjà
 * remplacé ; le serveur, qui y voit un rejeu, fermait la session partout et
 * l'application retombait sur l'écran de connexion (« retour à l'accueil »).
 */
function adoptStoredTokens(space: SessionSpace): boolean {
  const store = stores[space];
  // Jamais d'une nature de session à l'autre : un onglet d'entreprise ne reprend pas
  // la session de console ou d'assistance ouverte dans un autre onglet.
  const stored = readStore(space);
  if (stored.kind !== store.kind) return false;
  store.accessToken = stored.accessToken;
  store.refreshToken = stored.refreshToken;
  return true;
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && !event.key.startsWith('fa.')) return;
    for (const space of Object.keys(stores) as SessionSpace[]) adoptStoredTokens(space);
  });
}

// --- Entreprises d'un compte (décision du 25 octobre 2026) ---------------------

/** Adhésion que CET onglet affiche (sa base hors ligne, ses écrans) ; aucune hors session de membre. */
let tabMember: string | undefined;
/** Changement d'entreprise en cours dans cet onglet : plus aucun envoi jusqu'au nouveau profil. */
let held = false;
const memberListeners = new Set<(userId: string) => void>();

/** Désigne l'adhésion affichée par cet onglet (`auth.tsx`, à chaque profil appliqué). */
export function setTabMember(userId: string | undefined): void {
  tabMember = userId;
}

/** Suspend les envois de l'onglet pendant un changement d'entreprise (`auth.tsx`). */
export function holdRequests(value: boolean): void {
  held = value;
}

/** Les jetons de l'onglet viennent de passer à une autre adhésion (renouvellement de secours). */
export function onMemberChange(listener: (userId: string) => void): () => void {
  memberListeners.add(listener);
  return () => memberListeners.delete(listener);
}

/**
 * Routes de la session elle-même : elles servent justement à changer ou relire
 * l'adhésion. La vérification d'identité (`/auth/step-up…`) en est : une bascule
 * vers une entreprise qui exige la double authentification la demande en cours
 * de route, dans la session de l'entreprise quittée, puis la bascule est rejouée.
 */
const SESSION_PATHS = ['/auth/me', '/auth/switch', '/auth/refresh', '/auth/logout', '/auth/memberships', '/auth/step-up'];

/**
 * Un onglet n'envoie jamais rien avec les jetons d'une autre entreprise que
 * celle qu'il affiche : bascule en cours ici, ou faite dans un autre onglet
 * (jetons partagés). Refus typé « réseau » : la synchronisation garde ses
 * envois en attente, dans la base de leur entreprise, sans les marquer refusés.
 */
function blockedByMemberChange(path: string): string | undefined {
  if (currentSpace() !== 'internal' || stores.internal.kind !== 'internal') return undefined;
  if (SESSION_PATHS.some((prefix) => path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`))) {
    return undefined;
  }
  if (held) return t('compteEntreprises.enCours');
  const stored = storedMember();
  return tabMember && stored && stored !== tabMember ? t('compteEntreprises.autreOnglet') : undefined;
}

// --- Fin de session annoncée sur l'écran de connexion ---------------------------

/** Raisons d'une session fermée par le renouvellement, dites par l'écran de connexion. */
export type SessionEndNotice = 'appareil_retire' | 'double_authentification';
const SESSION_END_KEY = 'fa.session-end';

function noteSessionEnd(notice: SessionEndNotice): void {
  try {
    sessionStorage.setItem(SESSION_END_KEY, notice);
  } catch {
    /* sans stockage, l'écran de connexion s'ouvre sans explication */
  }
}

/** Raison de la dernière fin de session de cet onglet (écran de connexion), jusqu'à `clearSessionEndNotice`. */
export function sessionEndNotice(): SessionEndNotice | undefined {
  try {
    const notice = sessionStorage.getItem(SESSION_END_KEY);
    return notice === 'appareil_retire' || notice === 'double_authentification' ? notice : undefined;
  } catch {
    return undefined;
  }
}

export function clearSessionEndNotice(): void {
  try {
    sessionStorage.removeItem(SESSION_END_KEY);
  } catch {
    /* rien à effacer */
  }
}

/** Refus d'un compte dont le titulaire n'a pas encore prouvé l'adresse : « Mot de passe oublié » la prouve. */
export function isEmailUnproven(error: unknown): boolean {
  return error instanceof ApiError
    && (error.details as { reason?: unknown } | undefined)?.reason === 'email_unproven';
}

type Renewed = { accessToken: string; refreshToken: string; userId: string };

/**
 * Renouvellement refusé pour l'adhésion de la session (retirée, fermée par son
 * entreprise) : la session du compte vit peut-être encore. On la renouvelle
 * vers une autre entreprise du compte, au lieu de déconnecter la personne de
 * toutes. Une entreprise qui exige un second facteur que la session n'a pas
 * prouvé est passée (sans jeton d'accès valable, la vérification d'identité
 * passe par une nouvelle connexion). Un appareil retiré arrête tout : il a perdu
 * l'accès au compte entier, aucune autre entreprise ne s'ouvre.
 */
async function refreshElsewhere(path: string, refreshToken: string): Promise<
  Renewed | { refused: SessionEndNotice | undefined }
> {
  let stepUp = false;
  for (const userId of fallbackMembers()) {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken, userId }),
    });
    const body = (await res.json()) as ApiResponse<{ accessToken: string; refreshToken: string; userId?: string }>;
    if (body.ok && body.data.userId) {
      return { accessToken: body.data.accessToken, refreshToken: body.data.refreshToken, userId: body.data.userId };
    }
    if (!body.ok && body.code === 'appareil_revoque') return { refused: 'appareil_retire' };
    // Jeton de compte refusé lui-même : aucune autre entreprise n'y changera rien.
    if (!body.ok && body.code === 'unauthorized') return { refused: stepUp ? 'double_authentification' : undefined };
    if (!body.ok && body.code === 'step_up_required') stepUp = true;
  }
  return { refused: stepUp ? 'double_authentification' : undefined };
}

const refreshing: Partial<Record<SessionSpace, Promise<boolean>>> = {};

async function refreshOnce(space: SessionSpace): Promise<boolean> {
  const store = stores[space];
  // Un autre onglet a peut-être renouvelé pendant l'attente du verrou : ses
  // jetons sont alors les bons, inutile (et dangereux) de rejouer l'ancien.
  const presented = store.refreshToken;
  if (!adoptStoredTokens(space)) return false;
  if (store.refreshToken && store.refreshToken !== presented) return true;
  const path = REFRESH_PATH[store.kind];
  if (!store.refreshToken || !path) return false;
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: store.refreshToken }),
    });
    const body = (await res.json()) as ApiResponse<{ accessToken: string; refreshToken?: string; userId?: string }>;
    const follow = (renewed: Renewed): true => {
      // L'adhésion d'abord : un autre onglet qui lit les nouveaux jetons sait déjà qu'il doit suivre.
      writeStoredMember(renewed.userId);
      setTokens(renewed.accessToken, renewed.refreshToken, store.kind);
      for (const listener of memberListeners) listener(renewed.userId);
      return true;
    };
    if (body.ok) {
      // Adhésion du jeton disparue (retirée, suspendue, entreprise purgée) : le serveur a
      // renouvelé vers une autre entreprise du compte, qu'il nomme ; l'onglet la suit.
      if (store.kind === 'internal' && body.data.userId && body.data.refreshToken) {
        return follow({ accessToken: body.data.accessToken, refreshToken: body.data.refreshToken, userId: body.data.userId });
      }
      // Le portail ne fait pas tourner son refresh (24 h) : conserver l'existant.
      // Conserver aussi le TYPE de session, sinon le prochain refresh viserait
      // le mauvais endpoint.
      setTokens(body.data.accessToken, body.data.refreshToken, store.kind);
      return true;
    }
    if (store.kind === 'internal') {
      const elsewhere = body.code === 'unauthorized' || body.code === 'step_up_required'
        ? await refreshElsewhere(path, store.refreshToken)
        : { refused: body.code === 'appareil_revoque' ? 'appareil_retire' as const : undefined };
      if ('userId' in elsewhere) return follow(elsewhere);
      const notice = elsewhere.refused ?? (body.code === 'step_up_required' ? 'double_authentification' : undefined);
      if (notice) noteSessionEnd(notice);
    }
    setTokens(undefined, '', store.kind);
    return false;
  } catch {
    return false; // hors ligne : on garde le refresh token pour plus tard
  }
}

async function tryRefresh(): Promise<boolean> {
  const space = currentSpace();
  const store = stores[space];
  if (!store.refreshToken || !REFRESH_PATH[store.kind]) return false;
  refreshing[space] ??= (async () => {
    try {
      // Un seul renouvellement à la fois par espace, pour tous les onglets du navigateur.
      return typeof navigator !== 'undefined' && navigator.locks
        ? await navigator.locks.request(`myequip.session-refresh.${space}`, () => refreshOnce(space))
        : await refreshOnce(space);
    } finally {
      delete refreshing[space];
    }
  })();
  return refreshing[space];
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** FormData pour les uploads. */
  formData?: FormData;
  signal?: AbortSignal;
  /** Needed when an OAuth start sets a browser-bound cookie on a separate API origin. */
  credentials?: RequestCredentials;
  /** En-têtes propres à une route (`Idempotency-Key` d'une publication d'annonce). */
  headers?: Record<string, string>;
}

/** Envoie une requête authentifiée, avec un renouvellement de session sur 401. */
async function send(path: string, opts: RequestOptions): Promise<Response> {
  const attempt = async (): Promise<Response> => {
    const blocked = blockedByMemberChange(path);
    if (blocked) throw new ApiError('network_error', blocked, 'member_changed');
    const headers: Record<string, string> = { ...opts.headers };
    const { accessToken } = current();
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (!opts.formData) headers['content-type'] = 'application/json';
    const method = opts.method ?? 'GET';
    // Une mutation SANS corps envoie un objet vide : `content-type:
    // application/json` accompagné d'un corps vide est refusé par Fastify
    // (400 « Body cannot be empty… »). Bug réel reproduit en Phase G : éditeur
    // de rapport, acquittement d'urgence, replay de job, « tout lire »… tous
    // les POST sans corps du client échouaient dans le navigateur.
    const jsonBody = opts.body !== undefined
      ? JSON.stringify(opts.body)
      : method !== 'GET' ? '{}' : null;
    let response: Response;
    try {
      response = await fetch(`${BASE}${path}`, {
        method,
        headers,
        ...(opts.credentials ? { credentials: opts.credentials } : {}),
        body: opts.formData ?? jsonBody,
        ...(opts.signal ? { signal: opts.signal } : {}),
      });
    } catch (err) {
      // Message français unique (WEB1-10) ; le texte du navigateur (« Failed
      // to fetch », « Load failed »…) reste dans `details` pour le diagnostic.
      throw new ApiError('network_error', t('commun.horsLigneAction'), (err as Error).message);
    }
    return response;
  };

  let res = await attempt();
  // Une vérification d'identité manquante n'est pas une session expirée :
  // renouveler le jeton n'y changerait rien.
  if (res.status === 401 && !(await asksForStepUp(res)) && (await tryRefresh())) {
    res = await attempt();
  }
  return res;
}

async function asksForStepUp(res: Response): Promise<boolean> {
  const body = (await res.clone().json().catch(() => undefined)) as { code?: string } | undefined;
  return body?.code === 'step_up_required';
}

type Envelope<T> = ApiResponse<T> | undefined;

/**
 * Envoie la requête ; si le serveur demande une vérification d'identité et que
 * l'utilisateur la réussit, la rejoue une seule fois. La seconde réponse est
 * rendue telle quelle : jamais de boucle.
 */
async function sendVerified<T>(
  path: string,
  opts: RequestOptions,
  read: (res: Response) => Promise<Envelope<T>>,
): Promise<{ res: Response; body: Envelope<T> }> {
  const res = await send(path, opts);
  const body = await read(res);
  if (!body || body.ok || body.code !== 'step_up_required') return { res, body };
  // Espace interne uniquement : la console et l'assistance ont leur propre parcours.
  if (!stepUpHandler || current().kind !== 'internal' || path.startsWith('/auth/step-up')) return { res, body };
  if (!(await stepUpHandler(body.details as StepUpDetails | undefined))) {
    throw new ApiError('step_up_required', STEP_UP_CANCELLED, { cancelled: true });
  }
  const retried = await send(path, opts);
  return { res: retried, body: await read(retried) };
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { res, body } = await sendVerified<T>(path, opts,
    async (response) => (await response.json().catch(() => undefined)) as Envelope<T>);
  if (!body) {
    // 413 du proxy (corps au-delà de sa limite) : sans enveloppe, mais un refus du fichier, pas une panne.
    if (res.status === 413) throw new ApiError(PAYLOAD_TOO_LARGE, 'Fichier trop volumineux pour être envoyé.');
    throw new ApiError('internal_error', `Réponse invalide (${res.status})`);
  }
  if (!body.ok) {
    emitApiError(body.code);
    throw new ApiError(body.code, body.message, body.details);
  }
  return body.data;
}

/** Requête dont la réponse réussie est un fichier (PDF généré à la volée…). */
export async function apiBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const { res, body } = await sendVerified<never>(path, opts,
    // Un fichier n'a pas d'enveloppe : seule une réponse d'erreur est lue ici.
    async (response) => (response.ok ? undefined : (await response.clone().json().catch(() => undefined)) as Envelope<never>));
  if (res.ok) return res.blob();
  if (body && !body.ok) {
    emitApiError(body.code);
    throw new ApiError(body.code, body.message, body.details);
  }
  throw new ApiError('internal_error', `Réponse invalide (${res.status})`);
}

export async function uploadFile(
  file: File | Blob,
  kind: string,
  filename?: string,
  /** Route d'envoi d'un espace externe (sous-traitant) ; `/files` par défaut. */
  path = '/files',
  /** Compte auteur d'un média mis en file hors ligne : le serveur refuse si la session est celle d'un autre. */
  options: { ownerUserId?: string } = {},
): Promise<{ fileId: string; scanStatus: string; sizeBytes?: number; mimeType?: string }> {
  // Point de passage UNIQUE de tous les envois, replis hors-ligne compris :
  // c'est donc ici, et nulle part ailleurs, que la réduction préalable a sa
  // place. Elle ne fait qu'économiser la montée : le serveur normalise de
  // toute façon ce qu'il reçoit.
  const sent = await shrinkForUpload(file, kind);
  const name = filename ?? (file instanceof File ? file.name : 'fichier');
  const form = new FormData();
  form.append('kind', kind);
  // Avant le fichier : le serveur lit les champs qui le précèdent.
  if (options.ownerUserId) form.append('ownerUserId', options.ownerUserId);
  form.append('file', sent, uploadFilename(name, sent, file));
  return api(path, { method: 'POST', formData: form });
}

/** L'analyse antivirus n'a pas encore rendu son verdict : le même fichier peut être réessayé. */
export class ScanPendingError extends Error {}

/**
 * Attend le verdict de l'antivirus d'un fichier envoyé : un fichier n'est
 * utilisable qu'une fois déclaré sain. Lève une erreur lisible s'il est
 * refusé, ou si l'analyse dure plus d'une demi-minute (on peut réessayer).
 */
export async function waitForScan(fileId: string, statusPath = `/files/${fileId}/status`): Promise<void> {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    const status = await api<{ scan_status: string }>(statusPath);
    if (status.scan_status === 'sain') return;
    if (status.scan_status === 'infecte') {
      throw new Error('Le fichier a été refusé par l’analyse antivirus.');
    }
    if (status.scan_status === 'erreur_analyse') {
      throw new Error('L’analyse antivirus n’a pas pu aboutir : renvoyez le fichier.');
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1000));
  }
  throw new ScanPendingError('L’analyse antivirus prend plus de temps que prévu. Réessayez dans un instant.');
}
